import React, { useState, useEffect, useCallback } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import InfoCard from "@/components/ui/info-card";
import Dropdown from "@/components/ui/dropdown";
import { LeftChevron } from "@/components/icons";
import { reportsApi } from "../services/reports.api";
import type {
  ReportFilters,
  ParticipantPerformanceItem,
  ParticipantSortBy,
  EducationOfferingItem,
} from "../types/reports.types";
import type { TableColumn, SortOption } from "@/types/component";
import styles from "./ReportsEducationDetailPage.module.scss";

const SORT_BY_OPTIONS: { value: ParticipantSortBy; label: string }[] = [
  { value: "traineeName", label: i18n.t("reportsEducationDetail.traineeName") },
  { value: "title", label: "Unvan" },
  { value: "departmentName", label: "Birim" },
  { value: "progressPercent", label: i18n.t("reportsEducationDetail.progressPercent") },
  { value: "examScore", label: i18n.t("reportsEducationDetail.examScore") },
  { value: "certificateStatus", label: "Sertifika Durumu" },
  { value: "lastActivityAt", label: "Son Aktivite" },
];

const defaultDateFrom = "2025-01-01";
const defaultDateTo = "2025-12-31";

const ReportsEducationDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { educationId } = useParams<{ educationId: string }>();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<ReportFilters>({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    personnelType: "all",
    offeringScope: "all",
  });

  const [offerings, setOfferings] = useState<EducationOfferingItem[]>([]);

  const [summary, setSummary] = useState<{
    completionRate?: number;
    successScore?: number;
    certificateCount?: number;
    activeUserCount?: number;
  } | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [participants, setParticipants] = useState<ParticipantPerformanceItem[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsTotal, setParticipantsTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<ParticipantSortBy>("traineeName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!educationId) return;
    let cancelled = false;
    reportsApi
      .getEducationOfferings(educationId)
      .then((items) => {
        if (!cancelled) setOfferings(items);
      })
      .catch(() => {
        if (!cancelled) setOfferings([]);
      });
    return () => {
      cancelled = true;
    };
  }, [educationId]);

  const fetchSummary = useCallback(async () => {
    if (!educationId) return;
    setSummaryLoading(true);
    try {
      const res = await reportsApi.getEducationSummary(educationId, filters);
      const d = res?.data;
      setSummary(
        d
          ? {
              completionRate: (d as any).completionRate,
              successScore: (d as any).successScore,
              certificateCount: (d as any).certificateCount,
              activeUserCount: (d as any).activeUserCount,
            }
          : null,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("reportsEducationDetail.summaryLoadFailed"));
    } finally {
      setSummaryLoading(false);
    }
  }, [educationId, filters]);

  const fetchParticipants = useCallback(async () => {
    if (!educationId) return;
    setParticipantsLoading(true);
    try {
      const res = await reportsApi.getEducationParticipants(educationId, {
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch || undefined,
        sortBy,
        sortOrder,
        ...filters,
      });
      setParticipants(res.data as ParticipantPerformanceItem[]);
      setParticipantsTotal(res.total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("reportsEducationDetail.participantsLoadFailed"));
    } finally {
      setParticipantsLoading(false);
    }
  }, [educationId, currentPage, pageSize, debouncedSearch, sortBy, sortOrder, filters]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  const formatDate = (d: string | undefined) => {
    if (!d) return "—";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return d;
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const participantColumns: TableColumn<ParticipantPerformanceItem>[] = [
    {
      header: t("reports.participants.traineeName"),
      accessor: (r) => r.traineeName ?? "—",
      className: "trainee-name-column",
    },
    {
      header: t("reports.participants.title"),
      accessor: (r) => r.title ?? "—",
      className: "title-column",
    },
    {
      header: t("reports.participants.departmentName"),
      accessor: (r) => r.departmentName ?? "—",
      className: "department-column",
    },
    {
      header: t("reports.participants.progressPercent"),
      accessor: (r) =>
        r.progressPercent != null ? `%${r.progressPercent}` : "—",
      className: "progress-column",
    },
    {
      header: t("reports.participants.examScore"),
      accessor: (r) => (r.examScore != null ? String(r.examScore) : "—"),
      className: "exam-score-column",
    },
    {
      header: t("reports.participants.certificateStatus"),
      accessor: (r) => r.certificateStatus ?? "—",
      className: "certificate-column",
    },
    {
      header: t("reports.participants.lastActivityAt"),
      accessor: (r) => formatDate(r.lastActivityAt),
      className: "last-activity-column",
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("reports.educationDetailTitle")} />

      <div className={styles.detailPage}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/dashboard/reports")}
          aria-label={t("common.back")}
        >
          <LeftChevron width={20} height={20} />
        </button>
        <div className={styles.summaryCards}>
          {summaryLoading ? (
            <div className={styles.loadingPlaceholder}>
              {t("common.loading")}
            </div>
          ) : summary ? (
            <>
              <InfoCard
                title={t("reports.summary.completionRate")}
                value={
                  summary.completionRate != null
                    ? `%${summary.completionRate}`
                    : "—"
                }
              />
              <InfoCard
                title={t("reports.summary.successScore")}
                value={summary.successScore != null ? String(summary.successScore) : "—"}
              />
              <InfoCard
                title={t("reports.summary.certificateCount")}
                value={
                  summary.certificateCount != null
                    ? String(summary.certificateCount)
                    : "—"
                }
              />
              <InfoCard
                title={t("reports.summary.activeUserCount")}
                value={
                  summary.activeUserCount != null
                    ? String(summary.activeUserCount)
                    : "—"
                }
              />
            </>
          ) : null}
        </div>

        {offerings.length > 0 && (
          <div className={styles.offeringFilter}>
            <Dropdown
              label={t("reports.offering.label", { defaultValue: "Dönem" })}
              placeholder={t("reports.offering.all", { defaultValue: "Tümü" })}
              options={[
                { label: t("reports.offering.all", { defaultValue: "Tümü" }), value: "all" },
                ...offerings.map((o) => ({
                  label: t("reports.offering.item", {
                    defaultValue: "{{seq}}. dönem ({{status}})",
                    seq: o.sequenceNumber,
                    status: o.isActive
                      ? t("reports.offering.active", { defaultValue: "aktif" })
                      : t("reports.offering.closed", { defaultValue: "kapalı" }),
                  }),
                  value: String(o.sequenceNumber),
                })),
              ]}
              value={
                filters.offeringScope === "sequence" && filters.offeringSequenceNumber !== undefined
                  ? String(filters.offeringSequenceNumber)
                  : "all"
              }
              onSelect={(v) => {
                const raw = Array.isArray(v) ? v[0] : v;
                if (!raw || raw === "all") {
                  setFilters({ ...filters, offeringScope: "all", offeringSequenceNumber: undefined });
                } else {
                  const seq = Number(raw);
                  setFilters({
                    ...filters,
                    offeringScope: "sequence",
                    offeringSequenceNumber: Number.isFinite(seq) ? seq : undefined,
                  });
                }
                setCurrentPage(1);
              }}
            />
          </div>
        )}

        <PageHeader
          searchPlaceholder={t("reports.participants.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={setSearchTerm}
          showSort
          sortOptions={SORT_BY_OPTIONS.map((o) => ({
            label: o.label,
            value: o.value,
          }))}
          selectedSortValue={sortBy}
          onSort={(opt) => {
            const val = (opt as SortOption).value as ParticipantSortBy;
            setSortBy(val);
            setSortOrder(val === "lastActivityAt" ? "desc" : "asc");
            setCurrentPage(1);
          }}
        />

        <Table
          columns={participantColumns}
          data={participants}
          isLoading={participantsLoading}
          getRowId={(r) => r.id ?? String(Math.random())}
          currentPage={currentPage}
          pageSize={pageSize}
          totalRecords={participantsTotal}
          onPageChange={setCurrentPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setCurrentPage(1);
          }}
          emptyMessage={t("reports.noParticipants")}
        />
      </div>
    </DashboardLayout>
  );
};

export default ReportsEducationDetailPage;
